import { Box } from "@mui/system";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";
import { Button } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import SbuxSelect from "../../../../../components/SbuxSelect";
import SbuxTextField from "../../../../../components/SubxTextField";
import {
  getTextToSpeech,
  getResetTextToSpeech,
} from "../../../../../services/textToSpeech";
import {
  getTranslate,
  getResetTranslate,
} from "../../../../../services/translate";
import styles from "../../styles";
import { getTextLengthErrorMessage } from "../../utils";
import useCss from "../../../../../hooks/useCss";
import {
  getVoiceSelector,
  getLanguageSelector,
} from "../../../../../selectors/userInstanceSelector";
import { getAudioContentSelector } from "../../../../../selectors/textToSpeechSelectors";
import {
  getTranslatedContentSelector,
  getTranslatePendingSelector,
} from "../../../../../selectors/translateSelector";

interface VoiceItem {
  displayName?: string;
  id?: string;
  engine?: string;
  [key: string]: unknown;
}

interface LanguageConfig {
  code?: string;
  textToSpeechCode?: string;
  translateCode?: string;
  voice?: VoiceItem[];
  [key: string]: unknown;
}

interface MediaInfo {
  fileContent?: string;
  fileName?: string;
  file?: string;
  isTextToSpeech?: boolean;
  errorType?: string;
  [key: string]: unknown;
}

interface TranslatedContent {
  translations?: Array<{ translatedText?: string }>;
  [key: string]: unknown;
}

interface VoiceMenuItem {
  name?: string;
  value: VoiceItem;
}

interface Props {
  selectedLanguageCode?: string;
  handleMediaInfo: (info: MediaInfo | null) => void;
  handleDisableSaveBtn: (disabled: boolean) => void;
}

const MediaTextToSpeech = ({
  selectedLanguageCode,
  handleMediaInfo,
  handleDisableSaveBtn,
}: Props) => {
  const classes = useCss(styles);

  const voice = useAppSelector(getVoiceSelector);
  const language = useAppSelector(getLanguageSelector) as
    | LanguageConfig[]
    | null;
  const audioContent = useAppSelector(getAudioContentSelector) as string | null;
  const translatedContent = useAppSelector(
    getTranslatedContentSelector,
  ) as TranslatedContent | null;
  const isTranslatePending = useAppSelector(
    getTranslatePendingSelector,
  ) as boolean;

  const [selectedVoice, setSelectedVoice] = useState<VoiceItem | string>("");
  const [voices, setVoices] = useState<VoiceMenuItem[]>([]);
  const [text, setText] = useState("");
  const [blobUrl, setBlobUrl] = useState("");
  const [disableBtn, setDisableBtn] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const dispatch = useAppDispatch();
  const [textLengthError, setTextLengthError] = useState(false);
  const [translatedText, setTranslatedText] = useState("");
  const [hasTranslated, setHasTranslated] = useState(false);
  const [hasConverted, setHasConverted] = useState(false);

  const resetAudio = () => {
    dispatch(getResetTextToSpeech());
    dispatch(getResetTranslate());
    setBlobUrl("");
    setText("");
    setTranslatedText("");
    setTextLengthError(false);
    setHasTranslated(false);
    setHasConverted(false);
    handleDisableSaveBtn(false);
    if (audioRef.current) {
      audioRef.current.load();
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    resetAudio();
    handleDisableSaveBtn(false);
    handleMediaInfo(null);
  };

  const handleCreateOrUpdateTextToSpeech = (e: React.MouseEvent) => {
    e.preventDefault();

    const selectedLanguage = (language ?? []).find(
      (l: LanguageConfig) => l.code === selectedLanguageCode,
    );
    if (selectedLanguage && selectedLanguage.textToSpeechCode) {
      setDisableBtn(false);
      const voiceObj = selectedVoice as VoiceItem;
      const payload = {
        text: translatedText || text,
        languageCode: selectedLanguage.textToSpeechCode,
        voice: voiceObj?.id,
        engine: voiceObj?.engine,
        fileType: "audio/mp3",
      };

      dispatch(getTextToSpeech(payload));
      setDisableBtn(true);
      setHasConverted(true);
    } else {
      setDisableBtn(true);
    }
    return false;
  };

  useEffect(() => {
    if (audioContent) {
      const content = "data:audio/wav;base64," + audioContent;
      setBlobUrl(content);
      const result: MediaInfo = {
        fileContent: content,
        fileName: "record.wav",
        file: audioContent,
        isTextToSpeech: true,
      };
      handleMediaInfo(result);
    } else {
      setBlobUrl("");
    }
  }, [audioContent]);

  useEffect(() => {
    if (translatedContent && translatedContent.translations) {
      const translated =
        translatedContent.translations[0]?.translatedText || "";
      setTranslatedText(translated);
      setHasTranslated(true);
      setHasConverted(false);
      setDisableBtn(false);
    }
  }, [translatedContent]);

  useEffect(() => {
    resetAudio();
  }, []);

  useEffect(() => {
    let newVoices: VoiceMenuItem[] = [];
    if (selectedLanguageCode) {
      const selectedLanguage = (language ?? []).find(
        (l: LanguageConfig) => l.code === selectedLanguageCode,
      );
      if (selectedLanguage && selectedLanguage.textToSpeechCode) {
        setDisableBtn(false);
        const v = (language ?? []).find(
          (r: LanguageConfig) => r.code === selectedLanguageCode,
        );
        if (v && v.voice) {
          newVoices = v.voice.map((item: VoiceItem) => {
            return {
              name: item?.displayName,
              value: item,
            };
          });
          setSelectedVoice(v.voice[0]);
        } else {
          resetAudio();
          handleMediaInfo(null);
        }
      } else {
        setDisableBtn(true);
        setSelectedVoice("");
      }
    }

    setVoices(newVoices);
    setHasTranslated(false);
    setTranslatedText("");
    setHasConverted(false);
    setBlobUrl("");
    dispatch(getResetTranslate());
    dispatch(getResetTextToSpeech());
  }, [selectedLanguageCode, voice]);

  const onVoiceChange = (value: string) => {
    setDisableBtn(false);
    const voiceObj = voices.find((v, idx) => {
      const vId =
        (v.value as VoiceItem)?.id ??
        (v.value as VoiceItem)?.displayName ??
        String(idx);
      return vId === value;
    })?.value as VoiceItem | undefined;
    setSelectedVoice(voiceObj ?? value);
    setHasConverted(false);
    setBlobUrl("");
    setTranslatedText("");
    setHasTranslated(false);
    handleMediaInfo(null);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length > 2000) {
      setTextLengthError(true);
      handleDisableSaveBtn(true);
    } else {
      setTextLengthError(false);
      handleDisableSaveBtn(false);
    }
    setDisableBtn(false);
    setText(value);
    setHasTranslated(false);
  };

  const handleTranslate = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!text.trim()) return;

    const selectedLanguage = (language ?? []).find(
      (l: LanguageConfig) => l.code === selectedLanguageCode,
    );
    const translateLanguageCode =
      selectedLanguage?.translateCode || selectedLanguageCode || "en";

    const payload = {
      languageCode: translateLanguageCode,
      translations: [text],
      translateOnce: true,
      sourceLanguageCode: "auto",
    };

    dispatch(getTranslate(payload));
  };

  const voiceMenuItems = voices.map((v, idx) => ({
    name: v.name,
    value:
      (v.value as VoiceItem)?.id ??
      (v.value as VoiceItem)?.displayName ??
      String(idx),
  }));

  const voiceSelectValue =
    typeof selectedVoice === "string"
      ? selectedVoice
      : (() => {
          const idx = voices.findIndex((v) => v.value === selectedVoice);
          return idx >= 0
            ? (voiceMenuItems[idx]?.value ?? "")
            : ((selectedVoice as VoiceItem)?.id ??
                (selectedVoice as VoiceItem)?.displayName ??
                "");
        })();

  return (
    <Box className={classes.tabContent}>
      <Box className={classes.formControl}>
        {voices && voices.length ? (
          <>
            <SbuxSelect
              label={<SbuxTranslate>{`Choose a voice`}</SbuxTranslate>}
              disabled={!voices || !voices.length}
              menuItems={voiceMenuItems}
              defaultValue={voiceSelectValue}
              value={voiceSelectValue}
              handleSelectChange={(value) => onVoiceChange(value)}
            />
          </>
        ) : null}

        {!voices ||
          (!voices.length && (
            <Box className={classes.warningText}>
              <SbuxTranslate>{`No text to speech available for this language`}</SbuxTranslate>
            </Box>
          ))}
        <div
          className={
            !voices || !voices.length ? classes.disabledBox : undefined
          }
        >
          <Box>
            <SbuxTextField
              label={<SbuxTranslate>{`Source Text`}</SbuxTranslate>}
              variant={"outlined"}
              value={text}
              handleChange={handleTextChange}
              rows={4}
              multiline={true}
              maxLength={2000}
              errorMessage={
                textLengthError ? getTextLengthErrorMessage(2000) : null
              }
              isError={textLengthError}
              error={textLengthError}
              displayCounter={true}
            />
          </Box>
          {translatedText && (
            <Box sx={{ marginTop: 2 }}>
              <SbuxTextField
                label={<SbuxTranslate>{`Translated Text`}</SbuxTranslate>}
                variant={"outlined"}
                value={translatedText}
                rows={4}
                multiline={true}
                disabled={true}
              />
            </Box>
          )}
          <Box
            className={classes.textToSpeachActionContainer}
            sx={{ marginTop: 2, justifyContent: "flex-end" }}
          >
            <Button
              variant="contained"
              size="medium"
              onClick={handleTranslate}
              disabled={
                !text || isTranslatePending || textLengthError || hasTranslated
              }
            >
              <SbuxTranslate>
                {isTranslatePending ? `Translating...` : `Translate`}
              </SbuxTranslate>
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={handleCreateOrUpdateTextToSpeech}
              disabled={!text || disableBtn || textLengthError || hasConverted}
            >
              <SbuxTranslate>{`Convert to Speech`}</SbuxTranslate>
            </Button>
            <Button variant="outlined" size="medium" onClick={handleClear}>
              <SbuxTranslate>{`Clear`}</SbuxTranslate>
            </Button>
          </Box>
          {blobUrl && (
            <Box className={classes.t2sAudioPlayer}>
              <audio
                ref={(audio) => {
                  audioRef.current = audio;
                }}
                src={blobUrl}
                controls
              />
            </Box>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default MediaTextToSpeech;
